import mitt, {Emitter} from 'mitt';
import {
    generateAssistantDeveloperMessage,
    getAssistantFromStore,
    persistMessage,
    runCompletionStream
} from "@/helper/chat/assistantAPI";

import {ResponseMessageRichResponse, serializeRR} from "@/helper/chat/richResponses";
import {isWriting, KapitelToolCall, performToolCall, serializeTC} from "@/helper/chat/toolCalls";
import {AInesAssistantType} from "@/graphql/generated/graphql";
import {consoleErrorChat, consoleLogChat} from "@/helper/console";

import {StreamingThreadMessage, StreamingThreadMessageChanges, ThreadMessageStatus} from "@/helper/chat/threadMessage";
import {AssistantRunStatus} from "@/helper/chat/assistantRun/assistantRunStatus";
import {AssistantRunConfig} from "@/helper/chat/assistantRun/assistantRunConfig";
import {makeMessagePersistParams} from "@/helper/chat/chatBL";
import {Stream} from "openai/streaming";
import {ChatCompletionChunk} from "openai/resources";
import {AssistantRun} from "@/helper/chat/assistantRun/assistantRun";
import {kapitelErrorHandler} from "@/helper/error";
import OpenAI from "openai";
import {addMessageToThread, getMessagesFromThreadStore} from "@/helper/chat/threadStore";
import {generateAdditionalInstructions} from "@/helper/chat/additionalInstructions";
import {APIPromise} from "openai/src/core";
import {ChatCompletion} from "openai/src/resources/chat/completions";
import {s} from "vite/dist/node/types.d-aGj9QkWt";

export type ExpertAssistantRunEvents = {
    messageStreaming: StreamingThreadMessageChanges
    runCancelled: any;
    streamDone: void;
    singleMessageCompleted: void;
};

export class AssistantRunExpert extends AssistantRun {
    public mittExpert: Emitter<ExpertAssistantRunEvents>;

    public message : StreamingThreadMessage
    private toolCallsAcc : Array<OpenAI.ChatCompletionMessageToolCall> = []
    private messageAcc : string = ''

    constructor(
        public runConfig: AssistantRunConfig,
        protected userMessage: string,
        protected isScriptedContent: boolean,
        private optimisticExpertConfirmedPromise : Promise<boolean> | undefined = undefined
    ) {
        super(runConfig, userMessage, isScriptedContent);

        // init additional expert events
        this.mittExpert = mitt<ExpertAssistantRunEvents>();

        // init message
        this.message = new StreamingThreadMessage('', 'assistant')

        // TODO!
        // if there is a parallel expertChooser running confirming our existence we should react to a negative outcome
        if (this.optimisticExpertConfirmedPromise) {
            this.optimisticExpertConfirmedPromise
                .then(confirmed => {
                    if (!confirmed) {
                        consoleLogChat(' %s: optimistic expert choice not confirmed - aborting completion',AInesAssistantType[this.runConfig.assistantType]);
                        this.resetCurrentStream()
                        this.setState(AssistantRunStatus.CANCELLED)
                        this.mittExpert.emit('runCancelled')
                    }
                })
        }

        addMessageToThread(userMessage, this.runConfig.threadId)
            .then(() => {
                makeMessagePersistParams({
                    config: this.runConfig,
                    role: "user",
                    content: userMessage,
                    isScriptedContent: isScriptedContent
                }).then(persistMessage)
            })
            .then(async () => {
                // start with first stream
                this.setCurrentStream(await this.runCompletionStream())
            })

        this.setState(AssistantRunStatus.REQUESTED);

        // error handling
        this.mitt.on('runFailed', (reason) => {
            const msg = 'Run ' + this.getRunId() + ' failed: ' /* + this.getLastError()?.code + ' - ' + this.getLastError()?.message */
            const e = new Error(msg, {cause: reason});

            // log to chat console
            consoleErrorChat(msg, this, e, reason);

            // handle error (toast, sentry)
            kapitelErrorHandler(e)
        })
    }

    /*
    multi stream handling
     */

    private streams : Stream<ChatCompletionChunk>[] = []
    private currentStream: Stream<ChatCompletionChunk> | undefined = undefined
    protected resetCurrentStream() {
        if (this.currentStream) {
            this.currentStream.controller.abort()
            this.currentStream = undefined
        }
    }
    protected setCurrentStream(stream: Stream<ChatCompletionChunk>) {
        try {
            if (this.currentStream) {
                throw new Error('already currentStream attached - reset first')
            }

            consoleLogChat(' %s: response stream started', AInesAssistantType[this.runConfig.assistantType])

            // reset TCs
            this.toolCallsAcc = []
            // reset content/msg
            this.messageAcc = ''
            this.updateMessage('', undefined,'ready')

            this.processStream(stream)
            this.currentStream = stream

            // collect stream
            this.streams.push(stream)
        } catch (error) {
            // state: on content complete progress state from streaming > failed
            this.setState(AssistantRunStatus.FAILED)
            // streamComplete: event
            this.mitt.emit('runFailed', error)
        }
    }


    /*
    stream event management: attaches & detaches local stream event listeners

    private event handler bound to this by using =>
    referenced by on & off event management
     */

    /*
   stream event management: attaches & detaches local stream event listeners

   private event handler bound to this by using =>
   referenced by on & off event management
    */

    protected endlessWhitespaceCounter = 0;
    protected endlessWhitespaceRetryCounter = 0;

    protected firstEventDone = false
    protected async processStream(stream: Stream<ChatCompletionChunk>) {

        // const dummyChunksForTesting = [
        //     "{",
        //     '"text": "',
        //     'Hallo',
        //     '",',
        //     '"cot": "Ich muss keine Rich Response anhängen",',
        //     '"richResponses": ',
        //     "[]",
        //     "} {",
        //     '"text": "',
        //     'Hallo 2',
        //     '",',
        //     '"cot": "Ich muss keine Rich Response anhängen",',
        //     '"richResponses": []',
        //     "}"
        // ];

        for await (const chunk of stream) {
            const choice = chunk.choices[0];
        // for (const contentDelta of dummyChunksForTesting){
            // first response event
            if (!this.firstEventDone) {
                //state: progress state from INIT > RUNNING
                this.setState(AssistantRunStatus.RUNNING)
                this.firstEventDone=true
            }

            const isMultiMessage = (str: string):boolean => {
                // \s matches any whitespace character (space, newline, tab, etc.)
                // + requires one or more occurrences; use * if you want to allow zero occurrences
                const regex = /}\s+{/;
                return regex.test(str);
            }

            const delta = choice.delta
            const toolCallsDelta = delta.tool_calls || [] as OpenAI.ChatCompletionChunk.Choice.Delta.ToolCall[];
            const contentDelta = delta.content

            // bugfix for endless whitespaces
            if (delta && (!toolCallsDelta || toolCallsDelta.length === 0) && (!contentDelta || contentDelta.trim() === '')) {
                this.endlessWhitespaceCounter++
                console.warn(delta)
                if (this.endlessWhitespaceCounter > 20) {
                    this.endlessWhitespaceCounter = 0
                    this.endlessWhitespaceRetryCounter++
                    consoleErrorChat(' %s: response stream produced >20 whitespace chunks', AInesAssistantType[this.runConfig.assistantType])
                    this.resetCurrentStream()
                    if (this.endlessWhitespaceRetryCounter < 3) {
                        this.setCurrentStream(await this.runCompletionStream(this.endlessWhitespaceRetryCounter))
                    } else {
                        this.setState(AssistantRunStatus.FAILED)
                        this.mitt.emit('runFailed')
                    }
                }
            } else {
                this.endlessWhitespaceCounter = 0
            }

            // accumulate message
            if (contentDelta) {
                this.messageAcc += contentDelta

                if(isMultiMessage(this.messageAcc)){
                    console.log("multi message detected")
                    const index = this.messageAcc.lastIndexOf('{');
                    if (index !== -1) {
                        const firstMessage = this.messageAcc.slice(0, index);
                        const startOfSecondMessage = this.messageAcc.slice(index);

                        this.messageAcc = firstMessage
                        this.handleMessageStreamDone()
                        
                        this.mittExpert.emit('singleMessageCompleted')
                        
                        this.messageAcc = startOfSecondMessage

                        this.message = new StreamingThreadMessage('', 'assistant')
                        this.handleMessageStreamDiff(startOfSecondMessage)
                    }else {
                        // should not happen
                        kapitelErrorHandler("couldn't find opening brackets even though expected multi message")
                    }

                }else {

                    this.handleMessageStreamDiff(contentDelta)
                }


            }


            // accumulate tc
            for (const toolCallDelta of toolCallsDelta) {
                const { index } = toolCallDelta;

                if (!this.toolCallsAcc[index]) {
                    this.toolCallsAcc[index] = toolCallDelta as OpenAI.ChatCompletionMessageToolCall;
                }


                this.toolCallsAcc[index].function.arguments += toolCallDelta.function?.arguments;
            }

            // stop reasons
            if (choice.finish_reason) {
                this.mittExpert.emit('streamDone')

                switch (choice.finish_reason) {
                    case "stop":
                        this.handleMessageStreamDone()

                        this.mittExpert.emit('singleMessageCompleted')

                        // state: on content complete progress state from streaming > completed
                        this.setState(AssistantRunStatus.COMPLETED)
                        // streamComplete: event
                        this.mitt.emit('runComplete')
                        break;

                    case 'length':
                        // state: on content complete progress state from streaming > failed
                        this.setState(AssistantRunStatus.FAILED)
                        // streamComplete: event
                        this.mitt.emit('runFailed')
                        break;

                    case 'tool_calls':
                        await this.handleRunRequiresToolCalls()
                        break;
                }
            }
        }
    }


    /*
    text streaming & message assembly

    streams responseMessage.text - on oaiTextDelta look for 'text:' & '"' and take everything in between as streamed text
     */

    private updateMessage(text: string, richResponses: ResponseMessageRichResponse[] | undefined, status: ThreadMessageStatus | undefined = undefined) {
        const updatedAttributes = {} as StreamingThreadMessageChanges

        if (this.message.text !== text) {
            this.message.text = text
            updatedAttributes.text = text
        }

        if (this.message.richResponses !== richResponses) {
            this.message.richResponses = richResponses
            updatedAttributes.richResponses = richResponses
        }

        if (status && this.message.status !== status) {
            this.message.status = status
            updatedAttributes.status = status
        }

        this.mittExpert.emit('messageStreaming', updatedAttributes)
    }


    private handleMessageStreamDiff(currentDelta: string = '') {
        // state handling for text attribute streaming
        if(this.message.status === 'streaming' && currentDelta.includes('"')){
            // end of text attribute - update only status
            this.updateMessage(this.message.text, undefined,'textDone')
        } else if (this.message.status === 'ready' && (this.messageAcc.endsWith('"text": "') || this.messageAcc.endsWith('"text":"'))) {
            // beginning of text attribute - reset text and move status to streaming
            this.updateMessage('', undefined,'streaming')
        } else if (this.message.status === 'streaming') {
            let existingText = this.message.text;
            let appendDiff = currentDelta
            if (existingText.length > 2) {
                const lookBackTwo = existingText.substring(existingText.length-2,existingText.length)
                appendDiff = lookBackTwo + appendDiff
                existingText = existingText.substring(0, existingText.length-2)
            }

            appendDiff = appendDiff.replace(/\\n/g, "\n")

            // append delta of text attribute
            this.updateMessage(existingText + appendDiff, undefined)
        }
    }

    private handleMessageStreamDone() {
        // responseMessage.text, responseMessage.richResponse on content complete set final text & rich response
        let message
        try {
            message = JSON.parse(this.messageAcc)
        } catch {
            message = undefined
        }

        const text = message?.text || ''
        const richResponses = message?.richResponses || []

        consoleLogChat(' %s responded: %o %o %o',AInesAssistantType[this.runConfig.assistantType], text, richResponses.map(serializeRR), {accumulatedStreamedMessage: this.messageAcc})

        // expert assistant response message
        addMessageToThread(
            this.messageAcc,
            this.runConfig.threadId,
            "assistant"
        ).then(() => {
            makeMessagePersistParams({
                config: this.runConfig,
                role: "assistant",
                content: text,
                runId: this.getRunId(),
                richResponses: richResponses
            }).then(persistMessage)
        })



        this.updateMessage(text, richResponses, 'messageDone')
    }


    /*
    tool call handling
     */

    private toolCalls : KapitelToolCall[] = []

    public getMostRecentToolCall() : KapitelToolCall | undefined {
        return this.toolCalls.length > 0 ? this.toolCalls[this.toolCalls.length-1] : undefined
    }

    private async handleRunRequiresToolCalls() {
        const toolCalls = this.toolCallsAcc

        const kapitelToolCalls = toolCalls.map(toolCall => ({
            id: toolCall.id,
            function: toolCall.function.name,
            arguments: toolCall.function.arguments
        }))

        kapitelToolCalls.forEach(kapitelToolCall => {
            // collect
            this.toolCalls.push(kapitelToolCall)
            // emit event
            this.mitt.emit('runRequiresToolCall', kapitelToolCall)
        })
        this.mitt.emit('runRequiresToolCalls', kapitelToolCalls)

        this.setState(AssistantRunStatus.PENDING_TOOL_CALL)

        consoleLogChat(' %s: tool calls required: %s',AInesAssistantType[this.runConfig.assistantType], this.toolCalls.map(serializeTC).join(', '))

        // if there is a parallel expertChooser running confirming our existence we should wait for it
        if (
            this.optimisticExpertConfirmedPromise &&
            this.toolCalls.some(isWriting)
        ) {
            consoleLogChat(' %s: tool calls (writing) required && optimistic expert choice pending - deferring writing tool calls: %s',AInesAssistantType[this.runConfig.assistantType], this.toolCalls.filter(isWriting).map(serializeTC).join(', '))
            // wait for choice and don't execute tool calls if choice is negative
            if (!(await this.optimisticExpertConfirmedPromise)) {
                return
            }
        }

        // detach current stream - we are about to switch and in the meantime this run shouldn't do anything
        this.resetCurrentStream()

        // log tool call messages
        // expert assistant response requiering  tool calls
        addMessageToThread(
            null,
            this.runConfig.threadId,
            "assistant",
            toolCalls,
        ).then(
            ()=>makeMessagePersistParams({
                config: this.runConfig,
                runId: this.getRunId(),
                role: "assistant",
                toolCalls: toolCalls
            }).then(persistMessage)
        )


        // resolve tool calls
        const toolCallResponses: any[] = await Promise.all(
            kapitelToolCalls.map(
                async (kapitelToolCall: KapitelToolCall) =>{
                    return {
                        tool_call_id: kapitelToolCall.id,
                        output: await performToolCall(kapitelToolCall)
                    }
                }
            )
        )


        // const nextStream = await this.submitToolCallOutputsAndStream(kapitelToolCalls, toolCallResponses)
        consoleLogChat(' %s: tool calls submitting ' + toolCallResponses.length + ' responses: %O', AInesAssistantType[this.runConfig.assistantType], toolCallResponses)
        toolCallResponses.forEach((output) => {
            // expert assistant submitting tool calls outputs
            addMessageToThread(
                output.output,
                this.runConfig.threadId,
                "tool",
                null,
                output.tool_call_id
            ).then(
                () => makeMessagePersistParams({
                    config: this.runConfig,
                    role: "tool",
                    content: output.output,
                    toolCallId: output.tool_call_id,
                    runId: this.getRunId(),
                }).then(persistMessage)
            )
        })

        this.setCurrentStream(await this.runCompletionStream())
        this.setState(AssistantRunStatus.RUNNING)

    // case chunk.stop_reason === "function_call":
    //     // state: on content complete progress state from streaming > requires_action
    //     this.setState(AssistantRunStatus.REQUIRES_ACTION)
    //     // tool call: event
    //     this.mitt.emit('runRequiresToolCall', chunk.choices[0].delta.function_call)
    //     break;
    }



    private runCompletionStream = async (retryCount: number = 0) => {

        const promptMessage = await generateAssistantDeveloperMessage(this.runConfig)
        const threadMessages: OpenAI.ChatCompletionMessageParam[] = getMessagesFromThreadStore(this.runConfig.threadId)

        const promptInfo = getAssistantFromStore(this.runConfig)

        consoleLogChat(' %s: run completion stream with thread %O', AInesAssistantType[this.runConfig.assistantType], threadMessages)

        if (retryCount > 0) {
            // input fuzyying to prevent caching
            promptMessage.content = retryCount + ". Versuch:\n" + promptMessage.content
        }

        const completion = runCompletionStream({
            model: promptInfo.model,
            tools: (promptInfo.tools && promptInfo.tools.length > 0) ? promptInfo.tools : undefined,
            response_format: promptInfo.response_format,
            messages: [promptMessage as OpenAI.ChatCompletionMessageParam].concat(threadMessages),
        })

        this.eventTracker.track('requestStart')
        completion
            .then(() => this.eventTracker.track('responseStart'))
            .catch(() => this.eventTracker.track('cancelled'))

        const streamDone = () => {
            this.eventTracker.track('responseEnd')
            this.mittExpert.off('streamDone', streamDone)
        }
        this.mittExpert.on('streamDone', streamDone)

        const runFailed = () => {
            this.eventTracker.track('cancelled')
            this.mitt.off('runFailed', runFailed)
        }
        this.mitt.on('runFailed', runFailed)

        


        return completion
    }

}
